import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { forkJoin } from 'rxjs';
import { RmsModule } from '@rms';
import { Column, TableIcon } from '@interfaces';
import { CustomerCrmService, RmsMessageService, RmsPulldownService } from '@services';
import { OrdersDataService } from '../../../../../services/orders-data.service';
import { PaymentReceiptService } from '../../../../../services/payment-receipt.service';
import { PAYMENT_STATUSES } from '@shared/constants/payment-status';
import { PaymentFormComponent } from './payment-form/payment-form.component';
import { PaymentDetailsComponent } from './payment-details/payment-details.component';
import { ReceiptViewerComponent } from './receipt-viewer/receipt-viewer.component';

const AUTHORIZED_STATUS_IDS = [2, 3, 4];
const SETTLED_STATUS_IDS = [5, 6];
const PENDING_STATUS_IDS = [1, 2, 3, 4];

@Component({
  selector: 'app-customer-payments-tab',
  imports: [RmsModule],
  templateUrl: './customer-payments-tab.component.html',
  styleUrl: './customer-payments-tab.component.scss'
})
export class CustomerPaymentsTabComponent implements OnChanges {
  @Input() companyId!: number;
  @Input() canEdit = false;
  @Input() companyName?: string;

  Orders: any[] = [];
  Payments: any[] = [];
  OrderBalances: Record<number, { OrderTotal: number; OrderPaid: number; OrderBalanceDue: number }> = {};
  PendingPaymentsByOrder: Record<number, { OrderPaymentID: number; MethodName: string; StatusName: string; Amount: number }[]> = {};
  Loading = false;

  Columns: Column[] = [
    {
      key: 'OrderID', label: 'Order #', type: 'number', width: 10,
      clickable: true, onActionKey: 'viewReceiptForRow', onAction: (row) => this.viewReceiptForRow(row)
    },
    { key: 'OrderTotal', label: 'Order Total', type: 'currency', align: 'right', format: '1.2-2' },
    { key: 'OrderBalanceDue', label: 'Balance Due', type: 'currency', align: 'right', format: '1.2-2' },
    { key: 'MethodName', label: 'Payment Method', type: 'select', SearchType: 'select', dropdownSearchOptions: [] },
    {
      key: 'StatusName', label: 'Status',
      type: 'select', SearchType: 'select',
      dropdownSearchOptions: PAYMENT_STATUSES.map(s => ({ label: s.StatusName, value: s.StatusName })),
      cellClass: (row) => 'status-pill status-' + String(row.StatusName || '').toLowerCase()
    },
    { key: 'Amount', label: 'Payment Amount', type: 'currency', align: 'right', format: '1.2-2' },
    { key: 'FeeAmount', label: 'Fee Amount', type: 'currency', align: 'right', format: '1.2-2' },
    { key: 'TotalAmount', label: 'Total Amount', type: 'currency', align: 'right', format: '1.2-2' },
    { key: 'TransactionReference', label: 'Transaction Reference' },
    { key: 'MaskedReference', label: 'Masked Card/Account #' },
    { key: 'RequestedDate', label: 'Requested Date', type: 'date' },
    { key: 'ModifiedDate', label: 'Last Updated', type: 'date' },
  ];

  Icons: TableIcon[] = [
    { icon: 'visibility', title: 'View', action: (row) => this.viewPayment(row) },
    { icon: 'edit', title: 'Edit', action: (row) => this.editPayment(row) },
    { icon: 'send', title: 'Send Confirmation', action: (row) => this.sendConfirmation(row) },
  ];

  get totalPayments(): number {
    return this.sumWhere(() => true);
  }
  get authorizedAmount(): number {
    return this.sumWhere(p => AUTHORIZED_STATUS_IDS.includes(p.StatusID));
  }
  get settledAmount(): number {
    return this.sumWhere(p => SETTLED_STATUS_IDS.includes(p.StatusID));
  }
  get customerOrderTotal(): number {
    return this.Orders.reduce((sum, o) => sum + (Number(o.OrderTotal) || 0), 0);
  }

  get outstandingBalance(): number {
    return Math.max(0, this.customerOrderTotal - this.settledAmount);
  }

  constructor(
    private crm: CustomerCrmService,
    private ordersData: OrdersDataService,
    private message: RmsMessageService,
    private pulldown: RmsPulldownService,
    private paymentReceipt: PaymentReceiptService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['companyId']) {
      this.load();
      this.loadPaymentMethodOptions();
    }
  }

  // Payment Method reference data is customer-independent, so this only actually
  // needs to run once - guarded rather than re-fetching on every companyId change.
  private paymentMethodOptionsLoaded = false;
  private loadPaymentMethodOptions() {
    if (this.paymentMethodOptionsLoaded) return;
    this.paymentMethodOptionsLoaded = true;
    this.ordersData.getRefData({ keys: ['PaymentMethods'] }).then(({ PaymentMethods }) => {
      const methodColumn = this.Columns.find(c => c.key === 'MethodName');
      if (methodColumn) {
        methodColumn.dropdownSearchOptions = (PaymentMethods || []).map((m: any) => ({ label: m.MethodName, value: m.MethodName }));
      }
      this.Columns = [...this.Columns];
    });
  }

  load(checkFullyPaidOrderId?: number, receiptCheck?: { orderPaymentId: number; priorStatusId: number | null }) {
    if (!this.companyId) return;
    this.Loading = true;
    const wasFullyPaid = checkFullyPaidOrderId != null && this.isOrderFullyPaid(checkFullyPaidOrderId);
    forkJoin({
      orders: this.crm.getOrderHistory(this.companyId),
      payments: this.crm.getCustomerOrderPayments(this.companyId)
    }).subscribe({
      next: ({ orders, payments }) => {
        this.Orders = orders || [];
        this.Payments = payments || [];
        this.OrderBalances = this.buildOrderBalances();
        this.PendingPaymentsByOrder = this.buildPendingPaymentsByOrder();
        this.Payments = this.Payments.map(p => ({ ...p, ...(this.OrderBalances[p.OrderID] || {}) }));
        this.Loading = false;
        if (checkFullyPaidOrderId != null && !wasFullyPaid && this.isOrderFullyPaid(checkFullyPaidOrderId)) {
          this.promptOrderFullyPaid(checkFullyPaidOrderId);
        }
        if (receiptCheck) {
          const freshRow = this.Payments.find(p => p.OrderPaymentID === receiptCheck.orderPaymentId);
          if (freshRow) {
            this.paymentReceipt.generateIfNewlySettled(receiptCheck.priorStatusId, freshRow, this.companyName)
              .then(receiptBlobPath => {
                if (!receiptBlobPath) return;
                const idx = this.Payments.findIndex(p => p.OrderPaymentID === receiptCheck.orderPaymentId);
                if (idx < 0) return;
                this.Payments[idx] = { ...this.Payments[idx], ReceiptBlobPath: receiptBlobPath, ReceiptGeneratedDate: new Date().toISOString() };
                // New array reference so rms-table's [data] input actually picks up the
                // change - same reference-sensitivity lesson as loadPaymentMethodOptions().
                this.Payments = [...this.Payments];
              })
              .catch(err => console.error('Failed to generate payment receipt', err));
          }
        }
      },
      error: (err) => {
        this.message.error('Failed to load customer payments: ' + this.errorText(err));
        this.Loading = false;
      }
    });
  }

  private isOrderFullyPaid(orderId: number): boolean {
    const balance = this.OrderBalances[orderId];
    return !!balance && balance.OrderTotal > 0 && balance.OrderBalanceDue <= 0;
  }

  private promptOrderFullyPaid(orderId: number) {
    this.message.success(`Order #${orderId} is now fully paid - eligible for release.`);
  }

  openPaymentForm() {
    if (!this.canEdit) return;
    this.openPaymentFormPulldown('Add Payment', { Orders: this.Orders, OrderBalances: this.OrderBalances, PendingPaymentsByOrder: this.PendingPaymentsByOrder }, 'Payment saved', 'Failed to save payment', null);
  }

  // Order # column shortcut - pre-fills Order and Payment Method to match the clicked
  addPaymentForOrder(row: any) {
    if (!this.canEdit) return;
    this.openPaymentFormPulldown(
      'Add Payment',
      { Orders: this.Orders, OrderBalances: this.OrderBalances, PendingPaymentsByOrder: this.PendingPaymentsByOrder, OrderID: row.OrderID, PaymentMethodID: row.PaymentMethodID },
      'Payment saved', 'Failed to save payment', null
    );
  }

  editPayment(row: any) {
    if (!this.canEdit) return;
    this.openPaymentFormPulldown('Edit Payment', { ...row, Orders: this.Orders, OrderBalances: this.OrderBalances, PendingPaymentsByOrder: this.PendingPaymentsByOrder }, 'Payment updated', 'Failed to update payment', row.StatusID);
  }

  private openPaymentFormPulldown(title: string, data: any, successMessage: string, errorPrefix: string, priorStatusId: number | null) {
    const ref = this.pulldown.open(PaymentFormComponent, {
      title,
      width: 'clamp(480px, 55vw, 760px)',
      data
    });
    ref.afterClosed().subscribe((result: any) => {
      if (!result || result.cancelled) return;
      this.ordersData.saveOrderPayment(result).subscribe({
        next: (saveResponse: any) => {
          this.message.success(successMessage);
          this.load(result.OrderID, { orderPaymentId: saveResponse.OrderPaymentID, priorStatusId });
        },
        error: (err) => this.message.error(`${errorPrefix}: ` + this.errorText(err))
      });
    });
  }

  viewPayment(row: any) {
    const ref = this.pulldown.open(PaymentDetailsComponent, {
      title: 'Payment Details',
      width: '900px',
      maxWidth: '900px',
      data: { payment: row, canEdit: this.canEdit, companyName: this.companyName }
    });

    ref.afterClosed().subscribe(() => this.load());
  }


  sendConfirmation(row: any) {
    const recipient = row.AuthorizationRecipientEmail || 'the customer';
    this.message.info(`Placeholder: confirmation for Order #${row.OrderID} would be sent to ${recipient}. Email sent.`);
  }

  // Order # column click (repurposed from the old pre-fill-Add-Payment behavior, which
  // moved to a whole-row click) - opens this payment's stored receipt PDF inline in an
  // RMS pulldown, same pattern as payment-details.component.ts's viewReceipt().
  viewReceiptForRow(row: any) {
    if (!row.ReceiptBlobPath) {
      this.message.info(`No receipt has been generated yet for this payment.`);
      return;
    }
    this.pulldown.open(ReceiptViewerComponent, {
      title: `Receipt - Payment #${row.OrderPaymentID}`,
      width: '70vw',
      maxWidth: '900px',
      data: { path: row.ReceiptBlobPath }
    });
  }

  private buildOrderBalances(): Record<number, { OrderTotal: number; OrderPaid: number; OrderBalanceDue: number }> {
    const map: Record<number, { OrderTotal: number; OrderPaid: number; OrderBalanceDue: number }> = {};
    for (const o of this.Orders) {
      const total = Number(o.OrderTotal) || 0;
      map[o.OrderID] = { OrderTotal: total, OrderPaid: 0, OrderBalanceDue: total };
    }
    for (const p of this.Payments) {
      if (SETTLED_STATUS_IDS.includes(p.StatusID) && map[p.OrderID]) {
        map[p.OrderID].OrderPaid += Number(p.Amount) || 0;
      }
    }
    Object.values(map).forEach(entry => {
      entry.OrderBalanceDue = Math.max(0, entry.OrderTotal - entry.OrderPaid);
    });
    return map;
  }

  private buildPendingPaymentsByOrder(): Record<number, { OrderPaymentID: number; MethodName: string; StatusName: string; Amount: number }[]> {
    const map: Record<number, { OrderPaymentID: number; MethodName: string; StatusName: string; Amount: number }[]> = {};
    for (const p of this.Payments) {
      if (!PENDING_STATUS_IDS.includes(p.StatusID)) continue;
      if (!map[p.OrderID]) map[p.OrderID] = [];
      map[p.OrderID].push({ OrderPaymentID: p.OrderPaymentID, MethodName: p.MethodName, StatusName: p.StatusName, Amount: Number(p.Amount) || 0 });
    }
    return map;
  }

  private sumWhere(predicate: (p: any) => boolean): number {
    return this.Payments.filter(predicate).reduce((sum, p) => sum + (Number(p.Amount) || 0), 0);
  }

  private errorText(err: any): string {
    return err?.error?.error?.message || err?.message || 'Unknown error';
  }
}
